import { z } from 'zod';

import { CONFIG } from './indexedDB';

// Our CSP has no `unsafe-eval`, so zod's JIT compiler is unusable anyway. Left
// to discover that itself it probes with `new Function('')`, which the browser
// reports as a securitypolicyviolation even though zod catches the throw.
// Must run before the first schema below is constructed.
z.config({ jitless: true });

/**
 * Schema for agent-authored analytics specs.
 *
 * Keep in sync with the authoring guide served by the backend MCP tool
 * (backend/packages/@treams/mcp/src/tools/analytics.mcp.ts) and the worker
 * executor whitelist (worker.ts / query.ts).
 */

const STORE_NAMES = Object.keys(CONFIG) as [string, ...string[]];

export const PIPELINE_STAGES = [
	'$match',
	'$project',
	'$group',
	'$sort',
	'$limit',
	'$unwind',
	'$lookup',
	'$addFields',
	'$count',
] as const;

const storeName = z.enum(STORE_NAMES);

const pipelineStage = z.record(z.string(), z.unknown()).superRefine((stage, ctx) => {
	const keys = Object.keys(stage);

	if (keys.length !== 1) {
		ctx.addIssue({
			code: 'custom',
			message: 'Each pipeline stage must have exactly one $-key',
		});
		return;
	}

	if (!(PIPELINE_STAGES as readonly string[]).includes(keys[0])) {
		ctx.addIssue({
			code: 'custom',
			message: `Unsupported pipeline stage "${keys[0]}". Allowed: ${PIPELINE_STAGES.join(', ')}`,
		});
	}
});

const pipeline = z
	.array(pipelineStage)
	.min(1)
	.superRefine((stages, ctx) => {
		const lookups = stages.filter((stage) => '$lookup' in stage);

		for (const stage of lookups) {
			const lookup = stage.$lookup as Record<string, unknown> | undefined;
			const from = lookup?.from;

			if (typeof from !== 'string' || !CONFIG[from]) {
				ctx.addIssue({
					code: 'custom',
					message: `$lookup.from must be one of: ${STORE_NAMES.join(', ')}`,
				});
			}
		}
	});

export const COLUMN_TYPES = ['date', 'localized'] as const;

// Strict: unknown keys (valueGetter, cellRenderer, ...) are rejected outright,
// which is the serializability wall for anything function-like.
const columnDef = z.strictObject({
	field: z.string(),
	headerName: z.string().optional(),
	type: z.enum(COLUMN_TYPES).optional(),
	sort: z.enum(['asc', 'desc']).optional(),
	filter: z.boolean().optional(),
	flex: z.number().optional(),
	width: z.number().optional(),
});

const grid = z.strictObject({
	columnDefs: z.array(columnDef).min(1),
});

const chartDataset = z.strictObject({
	field: z.string(),
	label: z.string(),
});

const chart = z
	.strictObject({
		kind: z.enum(['bar', 'line', 'pie']),
		labelsField: z.string(),
		datasets: z.array(chartDataset).min(1),
		stacked: z.boolean().optional(),
		horizontal: z.boolean().optional(),
	})
	.superRefine((value, ctx) => {
		if (value.kind === 'pie' && value.datasets.length !== 1) {
			ctx.addIssue({
				code: 'custom',
				message: 'Pie charts require exactly one dataset',
			});
		}

		if (value.horizontal && value.kind !== 'bar') {
			ctx.addIssue({
				code: 'custom',
				message: '"horizontal" is only supported for bar charts',
			});
		}
	});

export const analyticsSpecSchema = z
	.strictObject({
		source: storeName,
		pipeline,
		grid: grid.optional(),
		chart: chart.optional(),
		// Mustache template rendered against the query result; see AnalyticsBlock.
		text: z.string().min(1).optional(),
	})
	.superRefine((value, ctx) => {
		if (!value.grid && !value.chart && !value.text) {
			ctx.addIssue({
				code: 'custom',
				message: 'A spec requires at least one of "grid", "chart" or "text"',
			});
		}
	});

export type AnalyticsSpec = z.infer<typeof analyticsSpecSchema>;
export type AnalyticsGridSpec = NonNullable<AnalyticsSpec['grid']>;
export type AnalyticsChartSpec = NonNullable<AnalyticsSpec['chart']>;
export type AnalyticsColumnDef = z.infer<typeof columnDef>;

export type AnalyticsSpecParseResult =
	| { ok: true; spec: AnalyticsSpec }
	| { ok: false; error: string };

/**
 * Parse and validate raw fenced-block text into an analytics spec.
 * @param text The raw text content of a fenced code block.
 */
export function parseAnalyticsSpec(text: string): AnalyticsSpecParseResult {
	let json: unknown;

	try {
		json = JSON.parse(text);
	} catch {
		return { ok: false, error: 'The spec is not valid JSON' };
	}

	const result = analyticsSpecSchema.safeParse(json);

	if (!result.success) {
		const issues = result.error.issues
			.map((issue) =>
				issue.path.length ? `${issue.path.join('.')}: ${issue.message}` : issue.message,
			)
			.join('; ');

		return { ok: false, error: issues };
	}

	return { ok: true, spec: result.data };
}
